import { useEffect, useMemo, useState } from "react";

import CustomGrid from "../CustomGrid";
import { gridStructure } from "constants/CustomGridStructure";

import { Box, Paper } from "@mui/material";

import { GridRowSelectionModel } from "@mui/x-data-grid";

import DeleteConfirmationModaL from "components/utils/DeleteConfirmationModal";
import { EnhancedTableToolbar } from "../EnhancedToolbar";

import { Group } from "types";
import { GROUPS_URL as BASE_URL } from "constants/urls";

import { ENTITY } from "constants/entities";
import Loader from "../Loader";
import useModals from "hooks/useModals";
import useCrud from "hooks/useCrud";
import { useSession } from "providers/SessionProvider";
import { STATUS_CODES } from "constants/index";
import { useToast } from "hooks/useToast";
import { useFilter } from "hooks/useFilter";
import { getClientName } from "helpers/getClientName";
import { exportToExcel } from "helpers/exportToExcel";
import { getUserFullName } from "helpers";
import AddGroupModal from "./AddGroupModal";
import EditGroupModal from "./EditGroupModal";

export default function GroupList() {
  const [selected, setSelected] = useState<GridRowSelectionModel>([]);

  const {
    addModalOpen,
    setAddModalOpen,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
  } = useModals();

  const {
    groups,
    addSessionData,
    updateSessionData,
    deleteSessionData,
    isLoadingUser,
    articles,
    users,
  } = useSession();

  const { showSnackBar } = useToast();

  const {
    createData: createGroup,
    deleteData: deleteGroup,
    updateData: updateGroup,
    loading,
    showToast,
    toastMessage,
    toastSeverity,
    setShowToast,
  } = useCrud<Group>(BASE_URL, { disableAutoFetch: true });

  const groupsExcelColumns = {
    Codigo: (ai: Group & { client_name?: string }) => ai.code ?? "-",

    Descripción: (ai: Group & { client_name?: string }) =>
      ai.description ?? "-",

    Cliente: (ai: Group & { client_name?: string }) =>
      ai.client_id === null ? "TODOS" : (ai.client_name ?? "-"),

    "Creado por": (ai: Group & { created_by_name?: string }) =>
      ai.created_by_name,
  } satisfies Record<string, (row: any) => any>;

  const handleExportGroupsExcel = () => {
    exportToExcel<Group>({
      data: filteredGroups,
      columns: groupsExcelColumns,
      sheetName: "Grupos de artículos",
      fileNamePrefix: "Grupos_articulos",
      headerRows: [
        ["Listado de grupos de articulos"],
        [`Filtro: ${filterText?.trim() ? filterText : "—"}`],
        [],
      ],
    });
  };

  const enrichedGroups = useMemo(() => {
    return groups.map((item) => {
      const client = item.client;
      const client_name =
        item.client_id === null ? "TODOS" : getClientName(client);
      const created_by_name = item.created_by
        ? getUserFullName(item.created_by, users)
        : "SISTEMA";

      return {
        ...item,
        client_name,
        created_by_name,
      };
    });
  }, [groups, users]);

  const {
    filterText,
    setFilterText,
    filteredData: filteredGroups,
  } = useFilter<Group & { client_name: string; created_by_name: string }>({
    data: enrichedGroups,
    fields: gridStructure[ENTITY.GROUPS].fields.map(({ field }) =>
      field === "client" ? { field: "client_name" } : { field },
    ),
  });

  const handleDeleteSelected = async () => {
    const failedDeletes: number[] = [];

    for (const id of selected) {
      const reponse = await deleteGroup(id, true, true);
      if (reponse.status !== STATUS_CODES.OK) {
        failedDeletes.push(Number(id));
      }
    }
    deleteSessionData(
      ENTITY.GROUPS,
      selected
        .filter((item) => !failedDeletes.includes(Number(item)))
        .map((item) => item.toString()),
    );

    // Borrar el grupo de todos los articulos
    for (const article of articles) {
      if (
        article.GroupArticles &&
        article.GroupArticles.find((ga) => selected.includes(ga.group_id))
      ) {
        updateSessionData(ENTITY.ARTICLES, {
          ...article,
          GroupArticles: article.GroupArticles.filter(
            (ga) => !selected.includes(ga.group_id),
          ),
        });
      }
    }

    setSelected([]);
  };

  const handleCreate = async (newGroup: Group) => {
    const response = await createGroup(newGroup, true, true);
    if (response.status === STATUS_CODES.OK) {
      addSessionData(ENTITY.GROUPS, response.data as Group);
    }
  };

  const handleEdit = async (groupToEdit: Group) => {
    const response = await updateGroup(groupToEdit.id, groupToEdit, true, true);
    if (response.status === STATUS_CODES.OK) {
      updateSessionData(ENTITY.GROUPS, groupToEdit);
    }
    setSelected([]);
  };

  useEffect(() => {
    if (showToast) {
      showSnackBar(toastMessage, toastSeverity);
      setShowToast(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showToast]);

  return (
    <>
      {isLoadingUser ? (
        <Loader isFirstLoad={true} isLoading={loading} />
      ) : (
        <>
          <Loader isFirstLoad={false} isLoading={loading} />
          <AddGroupModal
            open={addModalOpen}
            handleOpen={() => setAddModalOpen(!addModalOpen)}
            onCreate={handleCreate}
          />
          <DeleteConfirmationModaL
            open={deleteModalOpen}
            handleOpen={() => setDeleteModalOpen(!deleteModalOpen)}
            entityName={ENTITY.GROUPS}
            values={selected.map((item) => {
              const value = groups.find((ai) => ai.id === item)?.description;
              if (value) return value;
              return "";
            })}
            handleDelete={handleDeleteSelected}
          />
          {selected.length > 0 ? (
            <EditGroupModal
              open={editModalOpen}
              handleOpen={() => setEditModalOpen(!editModalOpen)}
              value={groups.find((ai) => ai.id === selected[0]) as Group}
              onEdit={handleEdit}
            />
          ) : null}
          <Box sx={{ width: "99%" }}>
            <Paper sx={{ width: "100%", mb: 2 }}>
              <EnhancedTableToolbar
                numSelected={selected.length}
                handleAddModalOpen={() => setAddModalOpen(!addModalOpen)}
                handleEditModalOpen={() => setEditModalOpen(!editModalOpen)}
                handleDeleteModalOpen={() =>
                  setDeleteModalOpen(!deleteModalOpen)
                }
                onFilterChange={setFilterText}
                onExportExcel={handleExportGroupsExcel}
              />
              <CustomGrid<Group>
                structure={gridStructure[ENTITY.GROUPS]}
                rows={filteredGroups}
                onEdit={handleEdit}
                selectionModel={selected}
                onRowSelection={setSelected}
              />
            </Paper>
          </Box>
        </>
      )}
    </>
  );
}
